import React, { useState, useEffect, useMemo } from 'react';
import Modal from './Modal';
import Button from './Button';
import { DisplayGroup, Employee, NameDisplayFormat, EmployeeRole } from '../../shared/types.js';
import { formatEmployeeName } from '../utils.js';

interface AssignGroupManagersModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (assignedManagerIds: string[]) => void;
    group: DisplayGroup;
    employees: Employee[];
}

const AssignGroupManagersModal: React.FC<AssignGroupManagersModalProps> = ({ isOpen, onClose, onSave, group, employees }) => {
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    useEffect(() => {
        if (isOpen) {
            setSelectedIds(group?.managerIds || []);
        }
    }, [isOpen, group?.id]);

    const sortedEmployees = useMemo(() => [...employees].filter(e => e.role !== EmployeeRole.INACTIVE || group.managerIds.includes(e.id)).sort((a, b) => (a.lastName || '').localeCompare(b.lastName || '') || (a.firstName || '').localeCompare(b.firstName || '')), [employees, group.managerIds]);
    const handleToggleEmployee = (id: string) => {
        setSelectedIds(prev =>
            prev.includes(id) ? prev.filter(uid => uid !== id) : [...prev, id]
        );
    };
    const handleSelectAll = () => setSelectedIds(employees.map(e => e.id));
    const handleSelectNone = () => setSelectedIds([]);
    const handleSave = () => {
        onSave(selectedIds);
        onClose();
    };
    return (
        <Modal isOpen={isOpen} onClose={onClose} title={`Assign Managers to "${group?.name || '...'}"`}>
            <div className="text-gray-900 dark:text-white">
                <p className="mb-4 text-gray-500 dark:text-gray-400">Select the employees who should be managers for this group.</p>
                <div className="flex justify-start gap-4 mb-2">
                    <Button size="sm" variant="ghost" onClick={handleSelectAll}>Select All</Button>
                    <Button size="sm" variant="ghost" onClick={handleSelectNone}>Select None</Button>
                </div>
                <div className="max-h-80 overflow-y-auto border border-gray-300 dark:border-gray-700 rounded-lg p-2 space-y-1 bg-gray-50 dark:bg-gray-900/50">
                    {sortedEmployees.map(emp => (
                        <label key={emp.id} className="flex items-center gap-3 p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer">
                            <input
                                type="checkbox"
                                checked={selectedIds.includes(emp.id)}
                                onChange={() => handleToggleEmployee(emp.id)}
                                className="w-4 h-4 bg-gray-200 dark:bg-gray-600 border-gray-300 dark:border-gray-500 rounded text-brand-secondary focus:ring-brand-secondary focus:ring-2 focus:ring-offset-0"
                            />
                            <span>{formatEmployeeName(emp, NameDisplayFormat.LAST_FIRST)}</span>
                        </label>
                    ))}
                </div>
                 <div className="flex justify-end gap-4 pt-4 mt-4 border-t border-gray-200 dark:border-gray-700">
                    <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
                    <Button type="button" onClick={handleSave}>Save Assignments</Button>
                </div>
            </div>
        </Modal>
    );
};

export default AssignGroupManagersModal;